import {
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  ViewChild,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as domtoimage from 'dom-to-image';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { BaseChartDirective } from 'ng2-charts';
import { ChartService } from '../../services/line-values-chart..service';
import { LineValuesKpi } from '../../types';
import { LineValuesModalComponent } from './line-values-modal/line-values-modal.component';

type AccessibleChartRow = {
  label: string;
  value: string;
};

let nextLineValuesId = 0;

@Component({
  selector: 'app-line-values',
  templateUrl: './line-values.component.html',
  providers: [ChartService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LineValuesComponent {
  @ViewChild(BaseChartDirective) chart: BaseChartDirective;

  @Input({ required: true }) set data(v: LineValuesKpi) {
    this.data$.set(v);
  }

  @Input() loading: boolean = true;

  @Input() total: number = 0;

  @Output() navigate = new EventEmitter<string>();

  @ViewChild('modalContent') modalContent: ElementRef | undefined;

  // #region     BEGIN ChartService
  readonly chartService$ = inject(ChartService);

  get data$(): WritableSignal<LineValuesKpi> {
    return this.chartService$.data$;
  }

  readonly updateChartEffect$ = effect(() => {
    this.data$();
    this.chart?.update();
  });
  // #endregion  END ChartService

  readonly chartDescId = `line-values-card-desc-${(nextLineValuesId += 1)}`;

  readonly chartTableTitleId = `line-values-card-table-title-${nextLineValuesId}`;

  readonly chartTitle$ = computed(() => this.data$()?.title || 'Line chart');

  readonly chartValueHeader$ = computed(() => {
    const chartData = this.chartService$.lineChartData$();

    return chartData.datasets[0]?.label || this.data$()?.yAxisLabel || 'Value';
  });

  readonly accessibleChartRows$ = computed<AccessibleChartRow[]>(() => {
    const chartData = this.chartService$.lineChartData$();
    const labels = chartData.labels ?? [];
    const values = chartData.datasets[0]?.data ?? [];

    return labels.map((label, index) => ({
      label: String(label),
      value: String(values[index] ?? ''),
    }));
  });

  readonly chartDescription$ = computed(() => {
    const title = this.chartTitle$();
    const dataPointCount = this.accessibleChartRows$().length;
    const dataPointLabel = dataPointCount === 1 ? 'data point' : 'data points';

    return `${title} line chart with ${dataPointCount} ${dataPointLabel}. The data table lists each label and value.`;
  });

  private readonly matDialog = inject(MatDialog);

  openModal(): void {
    this.matDialog.open<LineValuesModalComponent, LineValuesKpi>(
      LineValuesModalComponent,
      {
        width: 'calc(100vw - 1rem)',
        data: this.data$(),
        hasBackdrop: true,
        ariaLabelledBy: 'line-values-dialog-title',
        ariaDescribedBy: 'line-values-chart-desc',
      },
    );
  }

  downloadImage(): void {
    const contentElement = this.modalContent?.nativeElement as HTMLElement;

    if (contentElement) {
      const options = {
        width: contentElement.offsetWidth,
        height: contentElement.offsetHeight,
        scale: 1.5,
        style: {
          transform: 'scale(1)',
          transformOrigin: 'top center',
        },
      };

      domtoimage
        .toPng(contentElement, options)
        .then((dataUrl) => {
          const link = document.createElement('a');
          link.href = dataUrl;
          link.download = 'component-image.png';
          link.click();
        })
        .catch((error) => {
          console.warn('Error', error);
        });
    }
  }

  async downloadXlsx(): Promise<void> {
    const chartData = this.chartService$.lineChartData$();

    const data = [];
    const labels = chartData.labels ?? [];

    const headers = [
      'Label',
      ...chartData.datasets.map((dataset) => dataset.label),
    ];
    data.push(headers);

    labels.forEach((label, index) => {
      const row = [label];
      chartData.datasets.forEach((dataset) => {
        row.push(dataset.data[index] ?? 0);
      });
      data.push(row);
    });

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Chart Data');
    worksheet.addRows(data);
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
      'chart-data.xlsx',
    );
  }

  // Ensure responsive resize of chart
  @HostListener('window:resize')
  onWindowResize(): void {
    this.chart?.update();
  }
}
